import {useEffect, useRef, useState} from 'react';
import {GlobalPosterEditorJqueryElement} from '@Components/poster-editor/poster-editor.types';

export interface PosterDimensions {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
  x: number;
  y: number;
}

const initialDimensions: PosterDimensions = {
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: 0,
  height: 0,
  x: 0,
  y: 0,
};

function usePosterDimensions(): PosterDimensions {
  const [dimensions, setDimensions] = useState<PosterDimensions>(initialDimensions);
  const currentPosterElementRef = useRef<HTMLElement | null>(null);
  const currentWhiteboardContainerRef = useRef<HTMLElement | null>(null);

  useEffect((): (() => void) => {
    let cleanup: (() => void) | undefined;
    let intervalId: number | undefined;
    let checkIntervalId: number | undefined;
    let resizeObserver: ResizeObserver | undefined;

    const setupObservers = (): boolean => {
      const posterElement = window.posterEditor?.whiteboard?.HTMLElement;
      const whiteboardContainer = window.posterEditor?.elements[GlobalPosterEditorJqueryElement.WHITEBOARD_CONTAINER]?.get(0);

      if (!posterElement || !whiteboardContainer) {
        return false;
      }

      // If we're already observing the same elements, don't re-setup
      if (
        currentPosterElementRef.current === posterElement &&
        currentWhiteboardContainerRef.current === whiteboardContainer &&
        resizeObserver
      ) {
        return true;
      }

      // Clean up old observers if elements changed
      if (cleanup) {
        cleanup();
        cleanup = undefined;
      }

      // Update refs
      currentPosterElementRef.current = posterElement;
      currentWhiteboardContainerRef.current = whiteboardContainer;

      const updateDimensions = (): void => {
        const rect = posterElement.getBoundingClientRect();
        setDimensions({
          top: rect.top,
          left: rect.left,
          right: rect.right,
          bottom: rect.bottom,
          width: rect.width,
          height: rect.height,
          x: rect.x,
          y: rect.y,
        });
      };

      updateDimensions();

      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });

      resizeObserver.observe(posterElement);

      whiteboardContainer.addEventListener('scroll', updateDimensions);

      cleanup = (): void => {
        if (resizeObserver) {
          resizeObserver.disconnect();
          resizeObserver = undefined;
        }
        whiteboardContainer.removeEventListener('scroll', updateDimensions);
        currentPosterElementRef.current = null;
        currentWhiteboardContainerRef.current = null;
      };

      return true;
    };

    // Try to set up immediately
    if (setupObservers()) {
      // Set up periodic check for poster element changes
      checkIntervalId = window.setInterval(() => {
        const posterElement = window.posterEditor?.whiteboard?.HTMLElement;
        if (posterElement && posterElement !== currentPosterElementRef.current) {
          // Poster element changed, re-setup
          setupObservers();
        }
      }, 100);

      return (): void => {
        if (checkIntervalId !== undefined) {
          window.clearInterval(checkIntervalId);
        }
        cleanup?.();
      };
    }

    // If not available, poll until it is
    intervalId = window.setInterval(() => {
      if (setupObservers()) {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
          intervalId = undefined;
        }

        // Set up periodic check for poster element changes
        checkIntervalId = window.setInterval(() => {
          const posterElement = window.posterEditor?.whiteboard?.HTMLElement;
          if (posterElement && posterElement !== currentPosterElementRef.current) {
            // Poster element changed, re-setup
            setupObservers();
          }
        }, 100);
      }
    }, 50);

    return (): void => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      if (checkIntervalId !== undefined) {
        window.clearInterval(checkIntervalId);
      }
      cleanup?.();
    };
  }, []);

  return dimensions;
}

export default usePosterDimensions;

